import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, InputField, Modal } from '../UI';
import { Button } from '../UI/base/Button';
import modalStyles from '../UI/Modal.module.css';
import type { LocationResponse, UserDetailResponse } from '../../api/generated';
import {
  ACCOUNT_ADMIN_CHECKBOX_ID,
  EMAIL_PATTERN,
  USER_FORM_MODAL_WIDTH,
  buildUserRoleCheckboxId
} from './usersConstants';
import {
  LOCATION_ROLE_DESCRIPTION_KEYS,
  LOCATION_ROLE_LABEL_KEYS,
  LOCATION_ROLE_ORDER,
  LOCATION_ROLE_SHORT_LABEL_KEYS
} from '../../constants/locationRoles';
import type { LocationRole } from '../../constants/locationRoles';
import { readRoles, withRoleToggled } from '../../services/locationUserService';
import styles from './UserFormModal.module.css';

export interface LocationRoleValue {
  locationId: string;
  roles: LocationRole[];
}

export interface UserFormValues {
  email: string;
  firstName: string;
  lastName: string;
  isAccountAdmin: boolean;
  locationRoles: LocationRoleValue[];
}

interface UserFormModalProps {
  user: UserDetailResponse | null;
  locations: LocationResponse[];
  onSave: (values: UserFormValues) => void | Promise<void>;
  // Absent while creating: there is no account to reset until the user exists.
  onResetPassword?: () => void;
  onClose: () => void;
}

const buildInitialValues = (
  user: UserDetailResponse | null,
  locations: LocationResponse[]
): UserFormValues => ({
  email: user?.email ?? '',
  firstName: user?.firstName ?? '',
  lastName: user?.lastName ?? '',
  isAccountAdmin: user?.accountAdmin ?? false,
  locationRoles: locations.map((location) => {
    const existing = user?.roleAssignments.find(
      (assignment) => assignment.locationId === location.id
    );
    return {
      locationId: location.id,
      roles: existing ? readRoles(existing.roles) : []
    };
  })
});

export const UserFormModal = ({
  user,
  locations,
  onSave,
  onResetPassword,
  onClose
}: UserFormModalProps) => {
  const { t } = useTranslation();
  const isEditing = user !== null;
  const [form, setForm] = useState<UserFormValues>(() => buildInitialValues(user, locations));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.firstName.trim()) next.firstName = t('config-user-error-first-name');
    if (!form.lastName.trim()) next.lastName = t('config-user-error-last-name');
    if (!form.email.trim()) {
      next.email = t('config-user-error-email-required');
    } else if (!EMAIL_PATTERN.test(form.email.trim())) {
      next.email = t('config-user-error-email-invalid');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // Return the onSave promise so the Save button auto-disables until it settles.
  const handleSave = (): void | Promise<void> => {
    if (!validate()) return;
    return onSave(form);
  };

  const toggleRole = (locationId: string, role: LocationRole): void => {
    setForm((prev) => ({
      ...prev,
      locationRoles: prev.locationRoles.map((entry) =>
        entry.locationId === locationId
          ? { ...entry, roles: withRoleToggled(entry.roles, role) }
          : entry
      )
    }));
  };

  return (
    <Modal
      title={isEditing ? t('config-user-edit') : t('config-user-create')}
      onClose={onClose}
      width={USER_FORM_MODAL_WIDTH}
      footer={
        <div className={modalStyles.footerRow}>
          {isEditing && onResetPassword && (
            <div className={styles.footerStart}>
              <Button type="secondary" onClick={onResetPassword}>
                {t('config-user-reset-password')}
              </Button>
            </div>
          )}
          <Button type="secondary" onClick={onClose}>
            {t('save-modal-cancel')}
          </Button>
          <Button type="primary" onClick={handleSave}>
            {t('config-iv-save')}
          </Button>
        </div>
      }
    >
      <div className={styles.form}>
        <div className={styles.nameRow}>
          <InputField
            label={t('config-user-first-name')}
            required
            type="text"
            value={form.firstName}
            onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
            error={errors.firstName}
          />
          <InputField
            label={t('config-user-last-name')}
            required
            type="text"
            value={form.lastName}
            onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
            error={errors.lastName}
          />
        </div>

        <InputField
          label={t('config-user-email')}
          required
          type="text"
          value={form.email}
          disabled={isEditing}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          error={errors.email}
        />

        <Checkbox
          id={ACCOUNT_ADMIN_CHECKBOX_ID}
          isChecked={form.isAccountAdmin}
          onChange={(isAccountAdmin) => setForm((f) => ({ ...f, isAccountAdmin }))}
          label={t('config-user-account-admin')}
        />

        <div className={styles.rolesSection}>
          <span className={styles.sectionLabel}>{t('config-user-location-roles')}</span>
          {/* The roles grid stays fully editable whether or not the box above is
              ticked: administering the account grants nothing at a centre, so an
              administrator needs a role here like anyone else. */}
          <p className={styles.adminNote}>{t('config-user-roles-hint')}</p>
          {locations.length === 0 ? (
            <p className={styles.adminNote}>{t('config-user-no-locations')}</p>
          ) : (
            <div className={styles.grid}>
              <div className={`${styles.gridRow} ${styles.gridHeader}`}>
                <span className={styles.gridLocation}>{t('config-user-col-location')}</span>
                {LOCATION_ROLE_ORDER.map((role) => (
                  <span
                    key={role}
                    className={styles.gridRole}
                    title={`${t(LOCATION_ROLE_LABEL_KEYS[role])} — ${t(LOCATION_ROLE_DESCRIPTION_KEYS[role])}`}
                  >
                    {t(LOCATION_ROLE_SHORT_LABEL_KEYS[role])}
                  </span>
                ))}
              </div>
              {form.locationRoles.map((entry) => {
                const location = locations.find((l) => l.id === entry.locationId);
                const locationName = location?.name ?? entry.locationId;
                return (
                  <div key={entry.locationId} className={styles.gridRow}>
                    <span className={styles.gridLocation}>{locationName}</span>
                    {LOCATION_ROLE_ORDER.map((role) => (
                      <span key={role} className={styles.gridRole}>
                        <Checkbox
                          id={buildUserRoleCheckboxId(entry.locationId, role)}
                          isChecked={entry.roles.includes(role)}
                          onChange={() => toggleRole(entry.locationId, role)}
                          // Hidden, since the column header carries the visible
                          // text, but it still names the box for screen readers.
                          label={t('config-user-role-aria', {
                            role: t(LOCATION_ROLE_LABEL_KEYS[role]),
                            location: locationName
                          })}
                          isLabelHidden
                        />
                      </span>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
